var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { combineSlices, configureStore, createDynamicMiddleware, } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import { enableBatching, batchDispatchMiddleware } from 'redux-batched-actions';
import createSagaMiddleware from 'redux-saga';
import { combineReducers, getIsClient } from '../utils';
import { combineLazyReducers } from '../utils/combine-lazy-reducers';
import { createRootSaga } from './root-saga';
import { defaultRootReducers } from './default-root-reducers';
import { enrichStore } from './utils/enrich-store';
import { injectInitialAsyncSagas } from './utils/inject-initial-async-sagas';
var __DEV__ = process.env.NODE_ENV === "development"; // eslint-disable-line
export var createAppStore = function (_a) {
    var devTools = _a.devTools, router = _a.router, rootReducers = _a.rootReducers, rootSagas = _a.rootSagas, eventNameToCancelRequests = _a.eventNameToCancelRequests, initialState = _a.initialState, _b = _a.dependencies, dependencies = _b === void 0 ? {} : _b, _c = _a.extraMiddlewares, extraMiddlewares = _c === void 0 ? [] : _c, asyncReducers = _a.asyncReducers, asyncSagas = _a.asyncSagas, manualSagaStart = _a.manualSagaStart, getDefaultMiddlewareOptions = _a.getDefaultMiddlewareOptions;
    var isClient = getIsClient();
    var rootReducersPackage = __assign(__assign({}, rootReducers), defaultRootReducers);
    var asyncReducersPackage = __assign({}, asyncReducers);
    var sagaMiddleware = createSagaMiddleware();
    // создаем корневой редюсер прокидывая в него доп параметры
    var rootReducer = (initialState
        ? combineLazyReducers(__assign(__assign({}, asyncReducersPackage), rootReducersPackage), initialState)
        : combineReducers(__assign(__assign({}, asyncReducersPackage), rootReducersPackage)));
    var dynamicMiddleware = createDynamicMiddleware();
    var store = configureStore({
        devTools: devTools !== null && devTools !== void 0 ? devTools : (__DEV__ && isClient),
        preloadedState: initialState,
        reducer: enableBatching(rootReducer),
        middleware: function (getDefaultMiddleware) {
            var _a;
            return (_a = getDefaultMiddleware(getDefaultMiddlewareOptions)
                .prepend(dynamicMiddleware.middleware))
                .concat.apply(_a, __spreadArray([batchDispatchMiddleware, sagaMiddleware], __read(extraMiddlewares), false));
        },
    });
    setupListeners(store.dispatch);
    // создаем корневую сагу прокидывая в нее доп параметры
    var rootSaga = createRootSaga({
        rootSagas: rootSagas,
        router: router,
        eventNameToCancelRequests: eventNameToCancelRequests,
        store: store,
    });
    var middlewaresCache = new Set();
    var addMiddleware = function () {
        var middlewares = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            middlewares[_i] = arguments[_i];
        }
        return dynamicMiddleware.addMiddleware.apply(dynamicMiddleware, __spreadArray([], __read(middlewares.filter(function (middleware) {
            if (middlewaresCache.has(middleware))
                return false;
            middlewaresCache.add(middleware);
            return true;
        })), false));
    };
    var slicesCache = new Set();
    var injectSlices = function (slices) {
        slices.forEach(function (slice) { return slicesCache.add(slice); });
        store.replaceReducer(combineSlices.apply(void 0, __spreadArray([__assign(__assign({}, store.rootReducers), store.asyncReducers)], __read(slicesCache), false)));
        slices.forEach(function (slice) {
            return typeof slice.reducerPath === 'string' &&
                slice.middleware &&
                addMiddleware(slice.middleware);
        });
        store.dispatch({
            type: '@redux-core-modules/inject-slices',
            payload: slices.reduce(function (acc, slice) {
                if (typeof slice.middleware === 'function' &&
                    typeof slice.reducerPath === 'string') {
                    acc.api.push(slice.reducerPath);
                }
                else if (typeof slice.reducerPath === 'string') {
                    acc.slices.push(slice.reducerPath);
                }
                else {
                    Object.keys(slice).forEach(function (key) {
                        acc.reducers.push(key);
                    });
                }
                return acc;
            }, {
                api: [],
                slices: [],
                reducers: [],
            }),
        });
    };
    enrichStore({
        rootReducersPackage: rootReducersPackage,
        asyncReducersPackage: asyncReducersPackage,
        sagaMiddleware: sagaMiddleware,
        store: store,
        router: router,
        asyncSagas: asyncSagas,
        rootSagas: rootSagas,
        initialState: initialState,
        rootSaga: rootSaga,
        dependencies: dependencies,
        addMiddleware: addMiddleware,
        injectSlices: injectSlices,
    });
    if (asyncSagas) {
        injectInitialAsyncSagas({ asyncSagas: asyncSagas, store: store });
    }
    if (!manualSagaStart) {
        sagaMiddleware.run(rootSaga);
    }
    // возвращаем объект стора
    return store;
};
